import React, { useEffect, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { DateRange, Range, RangeKeyDict } from "react-date-range"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { Popover, Box, IconButton } from "@mui/material"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { DateFilterParams } from "../../../../../../../shared/components/table/useInitialDateFilters"
import { DeliveryListDateState } from "../OrderDeliveryList"
import HeaderFilter from "./HeaderFilter"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar } from "@fortawesome/pro-light-svg-icons"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        width: "16rem",
        cursor: "pointer",
    },
    popover: {
        padding: "1rem",
    },
    dateRange: {
        cursor: "pointer",
        color: theme.palette.tailwind.slate[900],
        fontWeight: 700,
        marginLeft: "0.5rem",
    },
    calendarIcon: {
        cursor: "pointer",
        color: theme.palette.tailwind.slate[900],
        paddingLeft: "0.563rem",
    },
}))

type DateRangeFilterProps = {
    value: DeliveryListDateState
    onChange: (newValue: { startDate: DateTime; endDate: DateTime }) => void
    paramNameStart: DateFilterParams
    paramNameEnd: DateFilterParams
    minDate?: DateTime
    maxDate?: DateTime
}

export const SelectDateRange = ({
    value,
    onChange,
    paramNameStart,
    paramNameEnd,
    minDate,
    maxDate,
}: DateRangeFilterProps) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const [searchParams, setSearchParams] = useSearchParams()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const initialStart = value.startDate ?? minDate ?? DateTime.now()
    const initialEnd = value.endDate ?? maxDate ?? initialStart

    const [state, setState] = useState<Range>({
        startDate: initialStart.toJSDate(),
        endDate: initialEnd.toJSDate(),
        key: "selection",
    })

    useEffect(() => {
        if (!value.startDate || !value.endDate) return

        setState((prev) => ({
            ...prev,
            startDate: value.startDate?.toJSDate(),
            endDate: value.endDate?.toJSDate(),
        }))
    }, [value.startDate, value.endDate])

    const handleDateChange = useCallback((ranges: RangeKeyDict) => {
        const selection = ranges["selection"]
        setState((prev) => ({
            ...prev,
            startDate: selection.startDate ?? prev.startDate,
            endDate: selection.endDate ?? prev.endDate,
        }))
    }, [])

    const handleClick = (event: React.MouseEvent<HTMLElement | SVGSVGElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        const jsStart = state.startDate
        const jsEnd = state.endDate

        const newStartDate = jsStart ? dateTimeFactory.fromMillis(jsStart.getTime()) : value.startDate
        const newEndDate = jsEnd ? dateTimeFactory.fromMillis(jsEnd.getTime()) : value.endDate

        const hasChanged =
            !value.startDate ||
            !value.endDate ||
            !newStartDate?.equals(value.startDate) ||
            !newEndDate?.equals(value.endDate)

        if (newStartDate && newEndDate && hasChanged) {
            onChange({ startDate: newStartDate, endDate: newEndDate })

            const nextParams = new URLSearchParams(searchParams)

            if (newStartDate) {
                nextParams.set(paramNameStart, newStartDate.toISODate() || "")
            } else {
                nextParams.delete(paramNameStart)
            }

            if (newEndDate) {
                nextParams.set(paramNameEnd, newEndDate.toISODate() || "")
            } else {
                nextParams.delete(paramNameEnd)
            }

            setSearchParams(nextParams)
        }

        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)

    const startLabel = state.startDate
        ? dateTimeFactory.formatDate(dateTimeFactory.fromMillis(state.startDate.getTime()))
        : ""
    const endLabel = state.endDate ? dateTimeFactory.formatDate(dateTimeFactory.fromMillis(state.endDate.getTime())) : ""
    const { classes: dateRangeClasses } = useStyles()

    return (
        <HeaderFilter
            label={
                <LocalizedStrict
                    id="meeting-service-delivery-list-period"
                    vars={{ startLabel, endLabel }}
                    elems={{
                        dateRange: (
                            <span onClick={handleClick} className={dateRangeClasses.dateRange}>
                                {startLabel} - {endLabel}
                            </span>
                        ),
                        icon: (
                            <FontAwesomeIcon onClick={handleClick} className={classes.calendarIcon} icon={faCalendar} />
                        ),
                    }}
                >
                    <>
                        Period:
                        <span>
                            {startLabel} - {endLabel}
                        </span>
                    </>
                </LocalizedStrict>
            }
        >
            <>
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                    <Box className={classes.popover}>
                        <DateRange
                            onChange={handleDateChange}
                            minDate={minDate?.toJSDate()}
                            maxDate={maxDate?.toJSDate()}
                            moveRangeOnFirstSelection={false}
                            ranges={[state]}
                            months={2}
                            direction="horizontal"
                        />
                    </Box>
                </Popover>
                {/* <Box onClick={handleClick} className={classes.calendarIcon}>
                    <FontAwesomeIcon icon={faCalendar} />
                </Box> */}
            </>
        </HeaderFilter>
    )
}
